import { observable, runInAction } from "mobx"

import { fetchDeleteFavorite } from "~/api/favorite/delete"
import { fetchPostFavorite } from "~/api/favorite/post"
import { fetchGetFavorites } from "~/api/favorites/get"
import { RecruitmentTypeBySnakeCase } from "~/consts"

type RecruitmentType = keyof typeof RecruitmentTypeBySnakeCase

export default class FavoriteStore {
  @observable newGradJobIds: Array<{ id: number; curated: boolean }> = []
  @observable shortInternshipIds: Array<number> = []
  @observable longInternshipIds: Array<number> = []
  @observable eventIds: Array<number> = []
  @observable magazineIds: Array<number> = []

  fetch = async () => {
    const favorites = await fetchGetFavorites()
    runInAction(() => {
      this.newGradJobIds = this._devideNewGradJob(favorites)
      this.shortInternshipIds = this._devideByCategory(favorites, "short_internship")
      this.longInternshipIds = this._devideByCategory(favorites, "long_internship")
      this.eventIds = this._devideByCategory(favorites, "event")
      this.magazineIds = this._devideByCategory(favorites, "magazine")
    })
  }

  toggleFavorite = async (category: RecruitmentType, id: number, isFavorited: boolean, curated?: boolean) => {
    const convertedCategory = RecruitmentTypeBySnakeCase[category]
    let newGradJobType: "curated" | "rich" | undefined = undefined
    if (convertedCategory === "new_grad_job") {
      newGradJobType = curated ? "curated" : "rich"
    }

    if (isFavorited) {
      await fetchDeleteFavorite({
        id,
        category_name: convertedCategory,
        new_grad_job_type: newGradJobType,
      })
      runInAction(() => {
        switch (convertedCategory) {
          case "new_grad_job":
            this.newGradJobIds = this.newGradJobIds.filter((data) => data.id !== id)
          case "short_internship":
            this.shortInternshipIds = this.shortInternshipIds.filter((shortInternshipsId) => shortInternshipsId !== id)
          case "long_internship":
            this.longInternshipIds = this.longInternshipIds.filter((longInternshipId) => longInternshipId !== id)
          case "event":
            this.eventIds = this.eventIds.filter((eventId) => eventId !== id)
          case "magazine":
            this.magazineIds = this.magazineIds.filter((magazineId) => magazineId !== id)
        }
      })
    } else {
      await fetchPostFavorite({
        id,
        category_name: convertedCategory,
        new_grad_job_type: newGradJobType,
      })
      runInAction(() => {
        switch (convertedCategory) {
          case "new_grad_job":
            this.newGradJobIds.push({ id: id, curated: curated || false })
          case "short_internship":
            this.shortInternshipIds.push(id)
          case "long_internship":
            this.longInternshipIds.push(id)
          case "event":
            this.eventIds.push(id)
          case "magazine":
            this.magazineIds.push(id)
        }
      })
    }
  }

  isFavorited = (id: number, category: RecruitmentType, curated?: boolean) => {
    const convertedCategory = RecruitmentTypeBySnakeCase[category]
    switch (convertedCategory) {
      case "new_grad_job":
        return this.newGradJobIds.some((data) => data.id === id && data.curated === curated)
      case "short_internship":
        return this.shortInternshipIds.includes(id)
      case "long_internship":
        return this.longInternshipIds.includes(id)
      case "event":
        return this.eventIds.includes(id)
      case "magazine":
        return this.magazineIds.includes(id)
    }
  }

  /** @deprecated 型安全ではないため */
  _devideByCategory = (favorites: any, category: string) => {
    const _favorites = Array.isArray(favorites) ? favorites : []
    return _favorites.reduce((result, favorite) => {
      if (favorite.category_name === category) {
        result.push(favorite.id)
      }
      return result
    }, [] as number[])
  }

  /** @deprecated 型安全ではないため */
  _devideNewGradJob = (favorites: any) => {
    const _favorites = Array.isArray(favorites) ? favorites : []
    return _favorites.reduce((result: { id: any; curated: any }[], favorite: any) => {
      if (favorite.category_name === "new_grad_job") {
        result.push({ id: favorite.id, curated: favorite.curated })
      }
      return result
    }, [])
  }
}
